/* ---------------------------------------------------------------------------
   GW purchase-journey behaviour (Piece 4).

   Two small, independent enhancements. Both are progressive: with this file
   blocked, the PDP still shows every tab panel and the cart drawer still opens,
   traps focus natively and closes on Esc.

     1. <gw-tabs>  — 02-COMPONENTS §11. Upgrades server-rendered panels into a
        real tablist. MANUAL activation (arrow to focus, Enter/Space to select):
        §11 is explicit that automatic activation "loses content for screen-
        reader users mid-scan".
     2. Cart drawer focus-on-open — 02-COMPONENTS §15.8 requires focus to land on
        the drawer HEADING. Horizon's <dialog>.showModal() gives us the trap, Esc
        and focus restore for free, but it parks focus on the first focusable
        child, which is the close button. Landing on the heading is what tells a
        screen-reader user where she arrived.
   --------------------------------------------------------------------------- */

class GwTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.gwReady === 'true') return;

    /** @type {HTMLButtonElement[]} */
    this.tabs = Array.from(this.querySelectorAll('[data-ref="tab"]'));
    /** @type {HTMLElement[]} */
    this.panels = Array.from(this.querySelectorAll('[data-ref="panel"]'));
    const list = this.querySelector('[data-ref="list"]');
    if (!list || this.tabs.length === 0 || this.tabs.length !== this.panels.length) return;

    list.setAttribute('role', 'tablist');
    const label = this.dataset.label;
    if (label) list.setAttribute('aria-label', label);

    this.tabs.forEach((tab, i) => {
      const panel = this.panels[i];
      if (!tab.id) tab.id = `${panel.id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.setAttribute('tabindex', '0');
      tab.addEventListener('click', () => this.select(i, true));
      tab.addEventListener('keydown', (event) => this.onKeyDown(event, i));
    });

    this.select(0, false);
    this.dataset.gwReady = 'true';
  }

  /**
   * @param {number} index
   * @param {boolean} moveFocus
   */
  select(index, moveFocus) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      // Roving tabindex: one tab stop for the whole list, arrows move within it.
      tab.setAttribute('tabindex', active ? '0' : '-1');
      this.panels[i].hidden = !active;
    });
    const tab = this.tabs[index];
    if (moveFocus && tab) {
      tab.focus();
      // §11: "horizontally scrollable on mobile with the active tab scrolled into view".
      if (typeof tab.scrollIntoView === 'function') {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  onKeyDown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    // Manual activation keeps the currently selected panel available while
    // browsing the tab labels. Native Enter/Space clicks activate the focused tab.
    this.tabs.forEach((tab, i) => tab.setAttribute('tabindex', i === next ? '0' : '-1'));
    this.tabs[next].focus();
    this.tabs[next].scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('gw-tabs')) customElements.define('gw-tabs', GwTabs);

/* --- Cart drawer: focus the heading on open (§15.8) -----------------------
   Horizon's DialogOpenEvent is a plain CustomEvent dispatched on the component
   with bubbles unset, so it never reaches document — the listener has to sit on
   each <cart-drawer-component>. */
function gwBindDrawerFocus(host) {
  if (!(host instanceof HTMLElement) || host.dataset.gwFocusBound === 'true') return;
  host.dataset.gwFocusBound = 'true';
  host.addEventListener('dialog:open', () => {
    const heading = host.querySelector('.cart-drawer__dialog .cart-drawer__heading');
    if (!(heading instanceof HTMLElement)) return;
    heading.setAttribute('tabindex', '-1');
    requestAnimationFrame(() => heading.focus({ preventScroll: true }));
  });
}

function gwBindAllDrawers() {
  document.querySelectorAll('cart-drawer-component').forEach(gwBindDrawerFocus);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', gwBindAllDrawers, { once: true });
} else {
  gwBindAllDrawers();
}


/* --- Accelerated checkout label -------------------------------------------
   Shopify hydrates the payment button after Liquid renders, so a server-side
   text replacement alone is temporary. The block owns a small custom element
   that reapplies the merchant-facing label after hydration or a variant-driven
   re-render, while leaving branded wallet buttons untouched. */
class GwAcceleratedCheckout extends HTMLElement {
  connectedCallback() {
    this.setButtonLabel();
    this.observer = new MutationObserver(() => this.setButtonLabel());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  setButtonLabel() {
    const button = this.querySelector('button.shopify-payment-button__button--unbranded');
    const label = this.dataset.buttonLabel || 'Buy Now';
    if (!(button instanceof HTMLButtonElement) || button.textContent?.trim() === label) return;

    button.textContent = label;
    button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('gw-accelerated-checkout')) {
  customElements.define('gw-accelerated-checkout', GwAcceleratedCheckout);
}

/* --- Quantity stepper: aria-disabled, not disabled (02-COMPONENTS §1.3) ----
   This used to be a MutationObserver here that watched the `disabled` attribute
   on every stepper button and swapped it for `aria-disabled` after the fact.
   Two things were wrong with it. It raced the component that owns the state —
   `QuantitySelectorComponent.updateButtonStates()` re-asserts `disabled` on
   every value change, variant change and cart update, and a re-rendered stepper
   was never bound at all. And the observer's own write re-entered the callback
   (removing `disabled` IS a `disabled` mutation), so it undid its own
   `aria-disabled` on the very next tick. A `disabled` minus button therefore
   still shipped, and a real Tab walk at qty 1 skipped it.

   The fix moved to the source: `component-quantity-selector.js` /
   `component-cart-quantity-selector.js` now set `aria-disabled` directly and
   guard the click handlers, so the rendered result is right at first paint and
   after every re-render, with no mirror to fall out of sync. */
